import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Sidebar } from './Sidebar';
import { LanguageSwitcher } from './LanguageSwitcher';
import { LoadingOverlay } from './LoadingOverlay';

export function Layout() {
  const { t, i18n } = useTranslation();
  // Cross-app docs link goes straight at the visitor's locale.
  // Docs URLs preserve the locale-code casing (/docs/es-ES/),
  // matching the directories we ship under docs/<lang>/.
  const currentLang = i18n.language || 'en';
  const docsHref = currentLang === 'en' ? '/docs/' : `/docs/${currentLang}/`;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <>
    <LoadingOverlay />
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Desktop sidebar */}
      <div className="hidden md:flex">
        <Sidebar />
      </div>

      {/* Mobile overlay sidebar */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div className="relative z-10 h-full flex-shrink-0">
            <Sidebar onClose={() => setMobileMenuOpen(false)} />
          </div>
          <div className="flex-1" onClick={() => setMobileMenuOpen(false)} />
          <div className="fixed inset-0 bg-black/50 -z-10" />
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top bar */}
        <header className="flex items-center justify-between gap-1 border-b border-gray-200 bg-white px-4 py-2.5 md:justify-end md:px-6">
          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 md:hidden"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M2 4.75A.75.75 0 0 1 2.75 4h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 4.75Zm0 10.5a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75a.75.75 0 0 1-.75-.75ZM2 10a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 10Z" clipRule="evenodd" />
            </svg>
          </button>

          <div className="flex items-center gap-1">
            <LanguageSwitcher />
            <Link
              to="/pricing"
              className="rounded-md px-3 py-1.5 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
            >
              {t('nav.pricing')}
            </Link>
            <a
              href={docsHref}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
            >
              {t('nav.docs')}
              <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 0 0-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 0 0 .75-.75v-4a.75.75 0 0 1 1.5 0v4A2.25 2.25 0 0 1 12.75 17h-8.5A2.25 2.25 0 0 1 2 14.75v-8.5A2.25 2.25 0 0 1 4.25 4h5a.75.75 0 0 1 0 1.5h-5Z" clipRule="evenodd" />
                <path fillRule="evenodd" d="M6.194 12.753a.75.75 0 0 0 1.06.053L16.5 4.44v2.81a.75.75 0 0 0 1.5 0v-4.5a.75.75 0 0 0-.75-.75h-4.5a.75.75 0 0 0 0 1.5h2.553l-9.056 8.194a.75.75 0 0 0-.053 1.06Z" clipRule="evenodd" />
              </svg>
            </a>
            <Link
              to="/profile"
              className="ml-2 flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
              </svg>
              <span className="hidden sm:inline">{t('nav.profile')}</span>
            </Link>
          </div>
        </header>
        {/* Main content */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
        {/*
          Pinned "Powered by BWS" attribution — mirrors the landing
          footer. Reinforces the connection between the product
          ("WallaWhats") and the WhatsApp sender name ("BWS
          Notifications"). Sits OUTSIDE <main> so it doesn't scroll
          with page content; always visible.
        */}
        <footer className="border-t border-gray-100 bg-white/80 backdrop-blur">
          <p className="px-4 py-3 text-center text-xs text-gray-400">
            <Trans
              i18nKey="footer.poweredBy"
              components={{
                bws: (
                  <a
                    href="https://www.bws.ninja"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-gray-500 no-underline hover:text-gray-700 transition-colors"
                  />
                ),
              }}
            />
          </p>
        </footer>
      </div>
    </div>
    </>
  );
}
